/* eslint-disable dot-notation */
import { DeviceCommand, KeyValuePair } from '@moderepo/mode-internal-apis';
import catalog from './modulesCatalog.json';



/**
 * This is where we define all CONSTANTS and UTIL functions used only by Sensor Cloud projects. We don't want to add these
 * to the global utils or constants files because if we ever need to stop supporting sensor cloud or remove sensor cloud code,
 * we can do it easily just buy deleting this file.
 */


/**
 * Because we have sensor module data for both Homes and Devices and they have different API call to fetch data therefore we need
 * to know which container a sensor module is belonged to when we are viewing a sensor module so that we can call the correct
 * API. All the components that are used for displaying sensor module data will need to have this prop and a smContainerId prop
 * instead of homeId or deviceId.
 */
export enum SensorModuleContainer {
    HOME = 'home',
    DEVICE = 'device',
}


/**
 * This is the interface for sensor module type definition. This  data structure contains information about sensor modules
 * e.g. their model, vendor, name, description, schema, etc...
 */
export interface SensorModuleType {
    readonly modelId: string;
    readonly vendor: string;
    readonly vendorModelId: string;
    readonly name: string;
    readonly description: string;
    readonly moduleSchema: readonly string[];

    readonly iconName?: string;
    readonly canChangeInterval?: boolean;
    readonly serviceId?: string;
    readonly moduleSchemaDisplayNames?: {[key: string]: string};
}



/**
 * This is the interface for a specific instance of sensor module
 */
export interface SensorModule {
    readonly id: string;
    readonly gatewayId: string;
    readonly modelId: string;
    readonly sensing: 'on' | 'off';
    readonly sensors: readonly string[];
    readonly moduleSchema?: readonly string[],
    readonly name?: string | undefined;
    readonly interval?: number;
}


/**
 * Interface for one Sensor info. NOTE: this data is created and used by the frontend only.
 * It is not returned by the backend through any API call
 */
export interface SCSensorInfo {
    readonly name: string;
    readonly enabled: boolean;
    readonly displayName?: string;
}



/**
 * An interface for key/value pair used for Sensor cloud project. For sensor cloud projects, we also use KeyValuePair however,
 * the value of the kv pair is a SensorModule data, not just any value.
 */
export interface SensorCloudKeyValuePair extends Omit<KeyValuePair, 'value'> {
    readonly value: SensorModule;
}


/**
 * Initialize the sensor module catalog
 */
export const sensorModuleCatalog = catalog.map((module: any): SensorModuleType => {
    return module as SensorModuleType;
}) as readonly SensorModuleType[];


/**
 * Create a map of sensor module type by modelId so that we can lookup sensor module type faster.
 */
export const sensorModuleCatalogMap = sensorModuleCatalog.reduce((result: Map<string, SensorModuleType>, moduleType: SensorModuleType) => {
    result.set(moduleType.modelId, moduleType);
    return result;
}, new Map<string, SensorModuleType>());



/**
 * This is a specific list of command actions used in Sensor Cloud projects. Each project can come up with their own set of actions and
 * these are what sensor cloud projects have defined. These are the type of actions you can send to sensor cloud project devices.
 */
enum SensorCloudCommandAction {
    LIST_SENSOR_MODULES = 'listSensorModules',
    GET_SENSOR_MODULE_STATUS = 'getSensorModuleStatus',
    START_DISCOVERY = 'startDiscovery',
    SYNC_CLOCK = 'syncClock',
    CHECK_AND_UPDATE_FIRMWARE = 'checkAndUpdateFirmware',
    START_SENSORS = 'startSensors',
    STOP_SENSORS = 'stopSensors',
    REBOOT_DEVICE = 'rebootDevice',
    CANCEL_REBOOT_DEVICE = 'cancelRebootDevice',
}


/**
 * This is an interface for a specific type of command used for Sensor Cloud. DeviceCommand is very generic and only has an action which can be
 * any string. A SensorCloudCommand is specific to Sensor Cloud project and it can only allow action to be one of the actions defined in the
 * SensorCloudCommandAction enum.
 */
export interface SensorCloudCommand extends DeviceCommand {
    readonly action: SensorCloudCommandAction;
}

interface ListSensorModulesCommand extends DeviceCommand {
    readonly action: SensorCloudCommandAction.LIST_SENSOR_MODULES;
    readonly show?: 'registered' | 'unregistered' | 'connected';
}

interface GetSensorModuleStatusCommand extends DeviceCommand {
    readonly action: SensorCloudCommandAction.GET_SENSOR_MODULE_STATUS;
    readonly sensorModuleId: string;
}

interface StartDiscoveryCommand extends DeviceCommand {
    readonly action: SensorCloudCommandAction.START_DISCOVERY;
    readonly timeout?: number;
}

interface SyncClockCommand extends DeviceCommand {
    readonly action: SensorCloudCommandAction.SYNC_CLOCK;
}


interface CheckAndUpdateFirmwareCommand extends DeviceCommand {
    readonly action: SensorCloudCommandAction.CHECK_AND_UPDATE_FIRMWARE;
}

interface StartSensorsCommand extends DeviceCommand {
    readonly action: SensorCloudCommandAction.START_SENSORS;
}


interface StopSensorsCommand extends DeviceCommand {
    readonly action: SensorCloudCommandAction.STOP_SENSORS;
}


interface RebootDeviceCommand extends DeviceCommand {
    readonly action: SensorCloudCommandAction.REBOOT_DEVICE;
    readonly delay?: number;
}


interface CancelRebootDeviceCommand extends DeviceCommand {
    readonly action: SensorCloudCommandAction.CANCEL_REBOOT_DEVICE;
}



export const SENSOR_MODULE_PREFIX: string = 'sensorModule';


/**
 * A function that returns a KV Store key used for storing Sensor Module info. Sensor cloud store sensor module info using Home KV
 * and Device KV and there is a specific KEY naming convention that sensor cloud use. This function will take a moduleID and return
 * a key using that naming convention.
 *
 * Use this function instead of trying to build a key using the SENSOR_MODULE_PREFIX and moduleId because if the naming convention
 * change, we just need to update this function.
 *
 * @param moduleId
 * @returns
 */
export const getSensorModuleKVStoreKey = (moduleId: string) => {
    return `${SENSOR_MODULE_PREFIX}${moduleId}`;
};


/**
 * Create a command to list sensor modules
 */
export const listSensorModules = (show?: 'registered' | 'unregistered' | 'connected'): ListSensorModulesCommand => {
    return {
        action: SensorCloudCommandAction.LIST_SENSOR_MODULES,
        show,
    };
};

export const getSensorModuleStatus = (sensorModuleId: string): GetSensorModuleStatusCommand => {
    return {
        action: SensorCloudCommandAction.GET_SENSOR_MODULE_STATUS,
        sensorModuleId,
    };
};

export const startDiscovery = (timeout?: number): StartDiscoveryCommand => {
    return {
        action: SensorCloudCommandAction.START_DISCOVERY,
        timeout,
    };
};

export const syncClock = (): SyncClockCommand => {
    return {
        action: SensorCloudCommandAction.SYNC_CLOCK,
    };
};

export const checkAndUpdateFirmware = (): CheckAndUpdateFirmwareCommand => {
    return {
        action: SensorCloudCommandAction.CHECK_AND_UPDATE_FIRMWARE,
    };
};

export const startSensors = (): StartSensorsCommand => {
    return {
        action: SensorCloudCommandAction.START_SENSORS,
    };
};

export const stopSensors = (): StopSensorsCommand => {
    return {
        action: SensorCloudCommandAction.STOP_SENSORS,
    };
};

export const rebootDevice = (delay?: number): RebootDeviceCommand => {
    return {
        action: SensorCloudCommandAction.REBOOT_DEVICE,
        delay,
    };
};

export const cancelRebootDevice = (): CancelRebootDeviceCommand => {
    return {
        action: SensorCloudCommandAction.CANCEL_REBOOT_DEVICE,
    };
};



/**
 * Convert sensing value to boolean. Because different developer can use different way to flag if a sensor is sensing
 * so we need to handle different "sensing" values. We won't be able to support all because we don't know how everyone
 * is going to do it so we will try our best to guess.
 */
export const sensingValueToBoolean = (value: string): boolean => {
    if (typeof value === 'string') {
        // return true if sensing value is one of these values
        return ['on', 'yes', 'true', 'sensing'].includes(value.toLowerCase());
    }
    return Boolean(value);
};


/**
 * Given a sensor module's data, return the list of sensors available for this module. There will be a few ways of
 * getting this list, from sensor module catalog, from sensor module data schema, or sensor module's sensors list.
 */
export const getAvailableSensorsList = (sensorModule: SensorModule): readonly string[] => {
    const sensorModuleType = sensorModuleCatalogMap.get(sensorModule.modelId);

    if (sensorModuleType) {
        // there is a sensor module type defined for this sensor module, get the list of sensors from the schema
        return [...sensorModuleType.moduleSchema];
    }

    // get the list of sensors from sensorModule's schema if it exist. If not, use the module's sensors array, this is the last resort
    return [...(sensorModule.moduleSchema ? sensorModule.moduleSchema : sensorModule.sensors)];
};



/**
 * Create timeSeries id base on the sensorModuleId and the sensor. This MUST match how the backend create time series id.
 */
export const getTimeSeriesId = (sensorModuleId: string, sensorValueName: string): string => {
    return `${sensorModuleId}-${sensorValueName}`.toLowerCase();
};


/**
 * Break a time series Id back into sensorModuleId and sensor. This is the reverse of the getTimeSeriesId function above
 */
export const parseTimeSeriesId = (timeSeriesId: string): {sensorModuleId: string, sensorValueName: string} => {
    const index = timeSeriesId.lastIndexOf('-');
    const sensorModuleId = timeSeriesId.substring(0, index);
    const sensorValueName = timeSeriesId.substring(index + 1);

    return {
        sensorModuleId,
        sensorValueName: sensorValueName ? sensorValueName.toUpperCase() : sensorValueName,
    };
};



/**
 * Sensor Cloud end-user app has inconsistent use of the sensor module data. For example, SCEval store deviceId in sensor module as 'gatewayID'
 * and it store the value as number. The correct field should be 'gatewayId' and the value should be type string. Therefore, we need to fix these
 * data before we can use them.
 *
 * @param sensorModules
 */
export const fixSensorModuleData = (sensorModule: SensorModule): SensorModule => {
    // Copy the current object
    const fixedObject = {
        ...sensorModule,
    };

    // Fix the gatewayID issue
    if (sensorModule['gatewayID']) {
        fixedObject.gatewayId = fixedObject['gatewayID'].toString();
        delete fixedObject['gatewayID'];
    }

    return fixedObject;
};
